import ldExtend from "lodash-es/extend";
import ldFindLast from "lodash-es/findLast";

import { Injectable, TemplateRef, Injector, OnDestroy, inject } from "@angular/core";
import { ComponentPortal, ComponentType, TemplatePortal } from "@angular/cdk/portal";
import {
    filter,
    map,
    take,
    takeUntil,
    concatMap,
    toArray,
    catchError,
    tap,
    share
} from "rxjs/operators";

import { LgOverlayService, IOverlayResultApi } from "../lg-overlay";
import {
    IDialogOptions,
    LG_DIALOG_DATA,
    IDialogShowFinalizer,
    DialogCloseInitiator
} from "./lg-dialog.types";
import { LgDialogHolderComponent } from "./lg-dialog-holder.component";
import { LgDialogRef } from "./lg-dialog-ref";
import { GuardsCheckEnd, Router } from "@angular/router";
import { from, Observable, of, Subject } from "rxjs";

const DEFAULT_DIALOG_OPTIONS = {
    title: "Dialog",
    allowClose: true,
    parameters: {}
};

const OVERLAY_OPTIONS = {
    class: "lg-overlay__disabled",
    hasBackdrop: true,
    trapFocus: true
};

@Injectable()
export class LgDialogService implements OnDestroy {
    private _injector = inject(Injector);
    private _overlayService = inject(LgOverlayService);
    private _parentService = inject(LgDialogService, { optional: true, skipSelf: true });

    constructor() {
        const router = inject(Router, { optional: true });

        if (this._parentService) {
            this._holderInstances = this._parentService._holderInstances;
            this._stack = this._parentService._stack;
        } else {
            this._holderInstances = {};
            this._stack = [];
            if (router) {
                router.events
                    .pipe(
                        // we should know the navigation will happen (ignoring errors), but while the old component is still visible
                        filter(event => event instanceof GuardsCheckEnd),
                        takeUntil(this._destroyed$)
                    )
                    .subscribe(() => this._onNavigation());
            }
        }
    }

    private _idCounter = 0;
    private _holderInstances: Record<string, LgDialogHolderComponent>;
    private _stack: Array<LgDialogRef<unknown>>;
    private _dialogsCloseStream$: Observable<boolean> | null = null;
    private readonly _destroyed$ = new Subject<void>();

    public show<T, D = any>(
        componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
        options: IDialogOptions<D>,
        dialogOptionsFinalizeCb?: IDialogShowFinalizer<T, D>
    ): LgDialogRef<T> | null {
        const dialogId = this._getDialogId();
        const dialogOptions: IDialogOptions<D> = ldExtend({}, DEFAULT_DIALOG_OPTIONS, options);

        // 1. show overlay
        const overlay: IOverlayResultApi = this._overlayService.show(dialogId, OVERLAY_OPTIONS);
        const dialogHolderComponentPortal = new ComponentPortal(
            LgDialogHolderComponent,
            dialogOptions.viewContainerRef
        );
        // 2. attach dialog holder portal to overlay and get reference to created LgDialogHolderComponent instance
        const dialogHolderComponentInstance: LgDialogHolderComponent = overlay.overlayRef.attach(
            dialogHolderComponentPortal
        ).instance;

        // 3. Create dialogRef which internally subscribes to dialogHolderComponent._beforeClosed
        const dialogRef = new LgDialogRef<T>(
            dialogId,
            dialogOptions,
            overlay,
            dialogHolderComponentInstance
        );
        this._stack.push(dialogRef);

        // 4. Attach component or templateRef to dialogHolderComponent
        if (componentOrTemplateRef instanceof TemplateRef) {
            const templatePortal = new TemplatePortal<T>(
                componentOrTemplateRef,
                dialogOptions.viewContainerRef!,
                <any>{
                    $implicit: dialogOptions.data,
                    dialogRef
                }
            );
            dialogHolderComponentInstance._attachTemplate(templatePortal);
        } else {
            const injector = this._createInjector<T>(
                dialogOptions,
                dialogRef,
                dialogHolderComponentInstance
            );
            const componentPortal = new ComponentPortal(
                componentOrTemplateRef,
                undefined,
                injector
            );
            dialogRef.componentInstance =
                dialogHolderComponentInstance._attachComponent(componentPortal).instance;
        }

        // 5. Get final dialog options. If dialogOptionsFinalizeCb is provided, it can modify dialogOptions before they are finally applied
        const optionsFinalizedSuccessfully = this._finalizeDialogOptions(
            dialogOptionsFinalizeCb,
            dialogOptions,
            dialogRef.componentInstance,
            (finalOptions: IDialogOptions<D> | undefined) => {
                finalOptions = finalOptions || dialogOptions; // falsy finalOptions are possible in case of incorrectly implemented dialogOptionsFinalizeCb

                dialogRef.applyFinalizedOptions(finalOptions);
                dialogHolderComponentInstance.initialize(
                    finalOptions,
                    finalOptions.relatedTo && this._holderInstances[finalOptions.relatedTo.id],
                    overlay.focusFirstTabbableElement
                );

                this._holderInstances[dialogId] = dialogHolderComponentInstance;

                dialogRef
                    .beforeClosed()
                    .pipe(take(1))
                    .subscribe(() => {
                        delete this._holderInstances[dialogId];
                        this._stack.splice(this._stack.indexOf(dialogRef), 1);
                    });
            }
        );

        if (!optionsFinalizedSuccessfully) {
            dialogHolderComponentInstance.initialize(dialogOptions, null, () => false);
            dialogRef.close(true);
            return null;
        }

        return dialogRef;
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    private _getDialogId(): string {
        if (this._parentService) return this._parentService._getDialogId();

        return "Dialog" + ++this._idCounter;
    }

    private _finalizeDialogOptions<T, D>(
        optionsFinalizerFn: IDialogShowFinalizer<T, D> | undefined,
        dialogOptions: IDialogOptions<D>,
        dialogComponentInstance: T | undefined,
        onOptionsFinalized: (finalizedOptions: IDialogOptions<D> | undefined) => void
    ): boolean {
        if (!optionsFinalizerFn) {
            onOptionsFinalized(dialogOptions);
            return true;
        }

        try {
            optionsFinalizerFn(dialogOptions, dialogComponentInstance, onOptionsFinalized);
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    private _createInjector<T>(
        options: IDialogOptions<any>,
        dialogRef: LgDialogRef<T>,
        holder: LgDialogHolderComponent
    ): Injector {
        const userInjector = options.viewContainerRef && options.viewContainerRef.injector;
        return Injector.create({
            parent: userInjector || this._injector,
            providers: [
                {
                    provide: LgDialogHolderComponent,
                    useValue: holder
                },
                {
                    provide: LG_DIALOG_DATA,
                    useValue: options.data
                },
                {
                    provide: LgDialogRef,
                    useValue: dialogRef
                }
            ]
        });
    }

    private _onNavigation(): void {
        // close the dialogs in loop in case the operation itself causes cascade of changes
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const dialog = ldFindLast(
                this._stack,
                ref => ref.visible && (ref.options.forceCloseOnNavigation ?? true)
            );
            if (!dialog) break;
            dialog.close(null, true);
        }
    }

    hasOpenDialogs(): boolean {
        return this._stack.length > 0;
    }

    // Try close all opened dialogs at one.
    // This method is designed to be used mainly from `LgDialogCloseControlGuard`.
    // A single shared observable aims to prevent cases when `tryCloseAllDialogs` is called from multiple places.
    // Dialogs closing process can be continuous, not instant.
    // There can be cases when `LgDialogCloseControlGuard` are assigned to a parent and to a child route
    // in different combinations and try to independently close all dialogs. So a single source of truth is preferred.
    tryCloseAllDialogs(initiator?: DialogCloseInitiator): Observable<boolean> {
        if (this._dialogsCloseStream$) {
            // dialogs closing is already in progress
            return this._dialogsCloseStream$;
        }
        if (!this.hasOpenDialogs()) {
            return of(true); // there are no opened dialogs
        }
        // close one by one from the last to the first
        const reversedStack = this._stack.slice().reverse();
        const dialogCloseStream$ = from(reversedStack).pipe(
            // 1. executing dialog closing one by one from last to first
            // Next dialog waits for previous dialog to be closed.
            concatMap(dialogRef => dialogRef.tryClose(initiator)),
            // 2. check whether dialog can be closed.
            map(closed => {
                if (closed) {
                    return true; // going to the next dialog (concatMap logic)
                } else {
                    // in a case when a dialog cannot be closed
                    // break concatMap execution chain by throwing an error
                    // e.g. don't try to close the rest of dialogs
                    throw new Error();
                }
            }),
            toArray(), // is called when all dialogs are closed successfully
            map((/* result is an array of true */) => true), // notify subscribers that all dialogs were successfully closed
            catchError(() => {
                // inform subscribers that not all dialogs were closed
                // mapping the exception to falsy value
                return of(false);
            }),
            tap(() => {
                // after final result is received - kill dialogs closing stream
                this._dialogsCloseStream$ = null;
            }),
            // making an observable hot, so all subscribers are using the same stream of data
            share()
        );

        this._dialogsCloseStream$ = dialogCloseStream$;

        return dialogCloseStream$;
    }
}
